import { useEffect, useMemo, useState } from "react";
import { Clock, AlertCircle, Box, DollarSign, Layers, ChevronDown, ChevronUp, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtSf } from "../lib/utils";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DateFilter from "../components/layout/DateFilter";
import DeptDrillModal from "../components/DeptDrillModal";
import MoldCategoryCard from "../components/MoldCategoryCard";
import MoldPerformanceCard from "../components/MoldPerformanceCard";
import { MoldManufacturedCard, MoldManufacturedTrendCard, MoldEfficiencyTrendCard } from "../components/MoldManufacturedCards";
import MoldJobAnalysisCard from "../components/MoldJobAnalysisCard";
import MoldHoursBreakdownCards from "../components/MoldHoursBreakdownCards";
import MoldNcrReportCard from "../components/MoldNcrReportCard";
import MoldNcrOpenCard from "../components/MoldNcrOpenCard";
import MoldCncMaterialsCard from "../components/MoldCncMaterialsCard";
import MoldJobBudgetCard from "../components/MoldJobBudgetCard";
import FinishJobPerformanceCard from "../components/FinishJobPerformanceCard";
import JobBudgetTrendCard from "../components/JobBudgetTrendCard";
import MoldOvertimeTrendCard from "../components/MoldOvertimeTrendCard";
import FinishStagesSection from "../components/FinishStagesSection";
import FiberglassStagesSection from "../components/FiberglassStagesSection";
import GfrcFpyLevelsCard from "../components/GfrcFpyLevelsCard";

const TABS = [
  { key: "MOLD",       label: "Mold" },
  { key: "FRAME",      label: "Frame" },
  { key: "GFRC",       label: "GFRC" },
  { key: "FINISH",     label: "Finish" },
  { key: "FIBERGLASS", label: "Fiberglass" },
];

const ACCENTS = {
  MOLD:       "#00D4FF",
  FRAME:      "#A78BFA",
  GFRC:       "#F472B6",
  FINISH:     "#34D399",
  FIBERGLASS: "#FBBF24",
};

function fmtThousand(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (abs >= 1000) return (n / 1000).toFixed(2) + "K";
  return Number(n).toLocaleString();
}
function fmtMoneyK(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1000) return `$${(n / 1000).toFixed(2)}K`;
  return `$${Number(n).toFixed(2)}`;
}
function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0.00";
  return `$${Number(n).toFixed(2)}`;
}

export default function Departments() {
  const { user } = useAuth();
  const isAdmin = (user?.role_name === "Administrator") || (user?.role === "admin");
  const userDepts = useMemo(
    () => (user?.departments || []).map((d) => String(d).toUpperCase()),
    [user?.departments],
  );
  const plantScope = (user?.plant_scope || "BOTH").toUpperCase();
  // Tab list filtered by user's allowed departments (admin sees all).
  const visibleTabs = useMemo(() => {
    if (isAdmin || userDepts.length === 0) return TABS;
    return TABS.filter((t) => userDepts.includes(t.key));
  }, [isAdmin, userDepts]);
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";

  const [tabState, setTab] = useState(() => visibleTabs[0]?.key || "MOLD");
  // Effective tab — falls back to first visible if user's scope no longer includes the selection.
  const tab = useMemo(() => {
    if (visibleTabs.some((t) => t.key === tabState)) return tabState;
    return visibleTabs[0]?.key || tabState;
  }, [visibleTabs, tabState]);
  const [filter, setFilter] = useState({ preset: "all" });
  // Effective Mexico view — auto-clamped to whatever the user's plant scope allows.
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = useMemo(() => {
    if (!canSeeMex) return false;
    if (!canSeeSLC) return true;
    return showMexicoState;
  }, [canSeeMex, canSeeSLC, showMexicoState]);
  const [kpis, setKpis] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [drill, setDrill] = useState({ open: false, kind: null, label: null });

  const accent = ACCENTS[tab] || "#00D4FF";

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [k, j] = await Promise.all([
          api.get(`/departments/${tab}/kpis`, { params }),
          api.get(`/departments/${tab}/jobs`, { params }),
        ]);
        if (cancelled) return;
        setKpis(k.data);
        setJobs(j.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tab, params]);

  const filteredJobs = useMemo(() => {
    if (!jobSearch.trim()) return jobs;
    const q = jobSearch.toLowerCase();
    return jobs.filter((j) => j.job.toLowerCase().includes(q));
  }, [jobs, jobSearch]);

  return (
    <div className="space-y-6" data-testid="departments-page">
      <header>
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">02 · Departments</div>
        <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Department Breakdown</h1>
        <p className="text-sm text-muted-foreground mt-1">Detailed metrics and performance analysis by department</p>
      </header>

      {/* Date filter + Mexico toggle (hidden when user's plant scope is single-plant) */}
      <div className="flex flex-wrap items-center gap-3">
        <DateFilter value={filter} onChange={setFilter} />
        {(tab === "MOLD" || tab === "FRAME" || tab === "GFRC" || tab === "FINISH" || tab === "FIBERGLASS") && canSeeSLC && canSeeMex && (
          <label
            className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-widest cursor-pointer select-none"
            data-testid="show-mexico-toggle"
            onClick={() => setShowMexico((s) => !s)}
          >
            <span className="text-muted-foreground">Show Mexico Plants</span>
            <span
              className="relative inline-flex h-5 w-9 items-center transition-colors"
              style={{ backgroundColor: showMexico ? accent : "#1F2937" }}
            >
              <span
                className="inline-block h-3 w-3 bg-white transition-transform"
                style={{ transform: showMexico ? "translateX(20px)" : "translateX(4px)" }}
              />
            </span>
          </label>
        )}
        {/* Locked plant-scope badge for non-admin users with a single-plant scope */}
        {!isAdmin && (!canSeeSLC || !canSeeMex) && (
          <span
            className="ml-auto text-[10px] tracking-widest uppercase font-mono px-2 py-1 border border-border bg-[#0A0B10] text-muted-foreground"
            data-testid="plant-scope-locked"
            title={`Your account is scoped to ${canSeeMex ? "Mexico" : "SLC"} plants only`}
          >
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </div>

      {/* Department tabs (filtered by user's allowed departments) */}
      {visibleTabs.length === 0 ? (
        <div className="bg-[#12141D] border border-border p-8 text-center text-sm text-muted-foreground" data-testid="no-dept-access">
          You don&apos;t have access to any department yet. Ask an administrator to assign you a department under Users.
        </div>
      ) : (
      <div className="bg-[#12141D] border border-border" data-testid="dept-tabs">
        <div className="flex">
          {visibleTabs.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                data-testid={`dept-tab-${t.key.toLowerCase()}`}
                onClick={() => { setTab(t.key); setExpanded(false); setJobSearch(""); }}
                className={`flex-1 py-3 text-sm tracking-wider uppercase transition-colors relative
                  ${active ? "text-white bg-[#1A1C28]" : "text-[#94A3B8] hover:text-white"}`}
              >
                {t.label}
                {active && (
                  <span
                    className="absolute bottom-0 left-1/4 right-1/4 h-[2px]"
                    style={{ backgroundColor: ACCENTS[t.key] }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* KPI cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="dept-kpis">
        <KpiCard accent={accent} loading={loading} testId="card-total-hours"
                 icon={<Clock size={14} />} label="Total Hours"
                 onClick={() => setDrill({ open: true, kind: "hours", label: "Total Hours · Breakdown" })}>
          <div className="font-heading font-bold text-4xl tabular-nums">
            {kpis ? `${Math.round(kpis.overtime_hours)}h` : "—"}
            {kpis && <span className="text-sm text-muted-foreground uppercase tracking-wider ml-2">OT</span>}
          </div>
          {kpis && (
            <div
              data-testid="dept-sub-hours"
              className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 mt-3 bg-[#0A0B10] border-l-2 text-[13px] font-mono font-semibold text-white/90 tabular-nums"
              style={{ borderLeftColor: accent }}
            >
              <span style={{ color: accent }}>{fmtThousand(kpis.total_hours)}</span>
              hrs total
            </div>
          )}
        </KpiCard>

        <KpiCard accent={accent} loading={loading} testId="card-overtime-pct"
                 icon={<AlertCircle size={14} />} label="Overtime %"
                 onClick={() => setDrill({ open: true, kind: "overtime", label: "Overtime · Breakdown" })}
                 iconColorOverride={kpis && kpis.overtime_pct > 10 ? "#FBBF24" : "#34D399"}>
          <div
            className="font-heading font-bold text-4xl tabular-nums"
            style={{ color: kpis && kpis.overtime_pct > 10 ? "#FBBF24" : "#34D399" }}
          >
            {kpis ? `${kpis.overtime_pct}%` : "—"}
          </div>
          {kpis && (
            <div
              data-testid="dept-sub-overtime"
              className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 mt-3 bg-[#0A0B10] border-l-2 text-[13px] font-mono font-semibold text-white/90 tabular-nums"
              style={{ borderLeftColor: kpis.overtime_pct > 10 ? "#FBBF24" : "#34D399" }}
            >
              <span style={{ color: kpis.overtime_pct > 10 ? "#FBBF24" : "#34D399" }}>{Math.round(kpis.overtime_hours)}h OT</span>
              <span className="text-white/60">/</span>
              {Math.round(kpis.total_hours)}h Total
            </div>
          )}
        </KpiCard>

        <KpiCard accent={accent} loading={loading} testId="card-production-sf"
                 icon={<Box size={14} />} label="Production SF"
                 onClick={() => setDrill({ open: true, kind: "production", label: "Production SF · Breakdown" })}>
          <div className="flex items-center gap-3">
            <div className="font-heading font-bold text-4xl tabular-nums">
              {kpis ? `${kpis.sf_per_hour}` : "—"}
              {kpis && <span className="text-sm text-muted-foreground uppercase tracking-wider ml-2">SF/HR</span>}
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
              Completed
            </span>
          </div>
          {kpis && (
            <div
              data-testid="dept-sub-production"
              className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 mt-3 bg-[#0A0B10] border-l-2 text-[13px] font-mono font-semibold text-white/90 tabular-nums"
              style={{ borderLeftColor: accent }}
            >
              <span style={{ color: accent }}>{fmtSf(kpis.production_sf)}</span>
              SF total
            </div>
          )}
        </KpiCard>

        <KpiCard accent={accent} loading={loading} testId="card-material-cost"
                 icon={<DollarSign size={14} />} label="Material Cost"
                 onClick={() => setDrill({ open: true, kind: "material", label: "Material Cost · Breakdown" })}>
          <div className="font-heading font-bold text-4xl tabular-nums">
            {kpis ? fmtMoney(kpis.cost_per_sf) : "—"}
            {kpis && <span className="text-sm text-muted-foreground uppercase tracking-wider ml-2">/SF</span>}
          </div>
          {kpis && (
            <div
              data-testid="dept-sub-material"
              className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 mt-3 bg-[#0A0B10] border-l-2 text-[13px] font-mono font-semibold text-white/90 tabular-nums"
              style={{ borderLeftColor: "#FBBF24" }}
            >
              <span style={{ color: "#FBBF24" }}>{fmtMoneyK(kpis.material_cost)}</span>
              total
            </div>
          )}
        </KpiCard>

        <KpiCard accent={accent} loading={loading} testId="card-pieces"
                 icon={<Layers size={14} />} label="Pieces"
                 onClick={() => setDrill({ open: true, kind: "pieces", label: "Pieces · By Bed" })}
                 footer={
                   <button
                     data-testid="pieces-dropdown-toggle"
                     onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }}
                     className="flex items-center gap-1 text-[11px] tracking-widest uppercase hover:text-white transition-colors"
                     style={{ color: accent }}
                   >
                     {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                     {expanded ? "Hide" : "Show"} jobs
                   </button>
                 }>
          <div className="font-heading font-bold text-4xl tabular-nums">
            {kpis ? `${kpis.pieces}/${kpis.pieces}` : "—"}
          </div>
          {kpis && (
            <div
              data-testid="dept-sub-pieces"
              className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 mt-3 bg-[#0A0B10] border-l-2 text-[13px] font-mono font-semibold text-white/90 tabular-nums"
              style={{ borderLeftColor: accent }}
            >
              <span style={{ color: accent }}>{kpis.jobs_count}</span>
              jobs
            </div>
          )}
        </KpiCard>
      </section>

      {/* Jobs expandable panel */}
      {expanded && (
        <section className="bg-[#12141D] border border-border" data-testid="jobs-panel">
          <div className="px-5 py-4 border-b border-border flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Layers size={16} style={{ color: accent }} />
              <h3 className="font-heading font-semibold">Jobs Breakdown · {TABS.find((t) => t.key === tab)?.label}</h3>
              <span className="text-xs text-muted-foreground ml-1">{jobs.length} jobs</span>
            </div>
            <div className="ml-auto relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="job-search-input"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                placeholder="Search jobs…"
                className="bg-[#0A0B10] border border-border pl-8 pr-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28] sticky top-0">
                <tr>
                  <th className="py-2.5 px-5">Job Name</th>
                  <th className="py-2.5 px-5 text-right">Pieces</th>
                  <th className="py-2.5 px-5 text-right">Total SF</th>
                  <th className="py-2.5 px-5 text-right">Shipped SF</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground text-xs uppercase tracking-widest">No jobs match</td></tr>
                )}
                {filteredJobs.map((j) => (
                  <tr key={j.job} className="border-t border-border/40 hover:bg-[#1A1C28]/40">
                    <td className="py-2.5 px-5 font-mono" style={{ color: accent }}>{j.job}</td>
                    <td className="py-2.5 px-5 font-mono text-right">{j.pieces}</td>
                    <td className="py-2.5 px-5 font-mono text-right">{fmtSf(j.total_sf)}</td>
                    <td
                      className="py-2.5 px-5 font-mono text-right"
                      data-testid={`dept-jobs-shipped-sf-${j.job}`}
                      style={{ color: (j.shipped_sf || 0) > 0 ? "#22D3EE" : "#475569" }}
                    >
                      {fmtSf(j.shipped_sf)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Mold Category Output — only on Mold tab */}
      {tab === "MOLD" && <MoldCategoryCard accent={accent} showMexico={showMexico} />}
      {tab === "MOLD" && <MoldPerformanceCard accent={accent} showMexico={showMexico} />}
      {tab === "MOLD" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MoldManufacturedCard showMexico={showMexico} />
          <MoldManufacturedTrendCard showMexico={showMexico} />
        </div>
      )}
      {tab === "MOLD" && <MoldEfficiencyTrendCard showMexico={showMexico} />}
      {tab === "MOLD" && <JobBudgetTrendCard dept="MOLD" showMexico={showMexico} />}
      {tab === "MOLD" && <MoldHoursBreakdownCards showMexico={showMexico} />}
      {tab === "MOLD" && <MoldOvertimeTrendCard showMexico={showMexico} />}
      {tab === "MOLD" && <MoldJobAnalysisCard showMexico={showMexico} />}
      {tab === "MOLD" && <MoldJobAnalysisCard showMexico={showMexico} phase="CNC" title="Job Analysis · CNC" testId="mold-job-analysis-cnc-card" />}
      {tab === "MOLD" && <MoldNcrReportCard showMexico={showMexico} />}
      {tab === "MOLD" && <MoldNcrOpenCard showMexico={showMexico} />}
      {tab === "MOLD" && <MoldCncMaterialsCard showMexico={showMexico} />}
      {tab === "MOLD" && <MoldJobBudgetCard showMexico={showMexico} />}

      {/* Frame department cards — reuse generalized components with dept="FRAME" */}
      {tab === "FRAME" && <MoldEfficiencyTrendCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <JobBudgetTrendCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldHoursBreakdownCards dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldOvertimeTrendCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldJobAnalysisCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldNcrReportCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldNcrOpenCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldCncMaterialsCard dept="FRAME" showMexico={showMexico} />}
      {tab === "FRAME" && <MoldJobBudgetCard dept="FRAME" showMexico={showMexico} />}

      {/* GFRC department cards — reuse generalized components with dept="GFRC" */}
      {tab === "GFRC" && <MoldEfficiencyTrendCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <JobBudgetTrendCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldHoursBreakdownCards dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldOvertimeTrendCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldJobAnalysisCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldNcrReportCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldNcrOpenCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldCncMaterialsCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <MoldJobBudgetCard dept="GFRC" showMexico={showMexico} />}
      {tab === "GFRC" && <GfrcFpyLevelsCard showMexico={showMexico} />}

      {/* FINISH department — mirrors Mold structure + adds 3 stage cards (Wash/Patch/Load) and 3 QC pipelines */}
      {tab === "FINISH" && <MoldEfficiencyTrendCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <JobBudgetTrendCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <MoldHoursBreakdownCards dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <MoldOvertimeTrendCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <FinishStagesSection showMexico={showMexico} />}
      {tab === "FINISH" && <MoldJobAnalysisCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <MoldNcrReportCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <MoldNcrOpenCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <MoldCncMaterialsCard dept="FINISH" showMexico={showMexico} />}
      {tab === "FINISH" && <FinishJobPerformanceCard showMexico={showMexico} />}

      {/* FIBERGLASS department — mirrors Mold structure + adds 2 stage cards (MFG / FG-Finish) */}
      {tab === "FIBERGLASS" && <MoldEfficiencyTrendCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <JobBudgetTrendCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldHoursBreakdownCards dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldOvertimeTrendCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <FiberglassStagesSection />}
      {tab === "FIBERGLASS" && <MoldJobAnalysisCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldNcrReportCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldNcrOpenCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldCncMaterialsCard dept="FIBERGLASS" showMexico={showMexico} />}
      {tab === "FIBERGLASS" && <MoldJobBudgetCard dept="FIBERGLASS" showMexico={showMexico} />}

      <DeptDrillModal
        open={drill.open}
        onClose={() => setDrill({ open: false, kind: null, label: null })}
        dept={tab}
        kind={drill.kind}
        label={drill.label}
        params={params}
        accent={accent}
      />
    </div>
  );
}

function KpiCard({ accent, icon, label, children, footer, loading, testId, iconColorOverride, onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`relative bg-[#12141D] border border-border p-5 overflow-hidden flex flex-col min-h-[200px] transition-colors ${onClick ? "cursor-pointer hover:border-[#00D4FF]/60 hover:bg-[#161823]" : ""}`}
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
          <span style={{ color: iconColorOverride || accent }}>{icon}</span>
          {label}
        </div>
      </div>
      <div className="mt-4 flex-1">
        {loading ? <div className="h-10 w-24 bg-[#1A1C28] animate-pulse" /> : children}
      </div>
      {footer && <div className="mt-3 pt-3 border-t border-border/40">{footer}</div>}
    </div>
  );
}
